import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import {
  AppointmentOutputArtifactTypesLookup,
  type AppointmentStatusEnum,
  AppointmentStatusTypesLookup,
  WorkerStatusTypesLookup,
  appointmentOutputArtifactTypeSchema,
  platformTypesSchema,
} from '@eluve/graphql-types';
import { specSchema } from '@eluve/sdui-types';

const c = initContract();

const createAppointmentOutputArtifactRequestSchema = z.object({
  type: appointmentOutputArtifactTypeSchema,
  customCharts: z.array(z.record(z.unknown())).optional(),
  immediate: z.boolean().optional(),
  force: z.boolean().optional(),
});

export type CreateAppointmentOutputArtifactRequest = z.infer<
  typeof createAppointmentOutputArtifactRequestSchema
>;

export const artifactCitationSchema = z.object({
  text: z.string(),
  source: z.object({
    sourceType: z.string(),
    sourceDocId: z.string(),
  }),
  startIndex: z.number().optional(),
  endIndex: z.number().optional(),
});

export type ArtifactCitation = z.infer<typeof artifactCitationSchema>;

export const structuredCitationSchema = z.object({
  id: z.string(),
  type: z.string(),
  date: z.string().nullable(),
  title: z.string().optional(),
  data: z.unknown(),
});

export type StructuredCitation = z.infer<typeof structuredCitationSchema>;

export const previsitInsightSchema = z.object({
  title: z.string().describe('Short insight title (3-6 words)'),
  description: z.string().describe('Brief description of the insight'),
  citationIds: z
    .array(z.string())
    .describe('IDs of citations that support this insight'),
  citedTextSnippets: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      'Map from citationId to exact quoted text from source documents supporting this insight',
    ),
});

export type PrevisitInsight = z.infer<typeof previsitInsightSchema>;

export const structuredPrevisitInsightsBaseSchema = z.object({
  patientSummary: z
    .string()
    .describe('Concise patient summary (age, gender, key conditions)'),
  previsitInsights: z.array(previsitInsightSchema),
});

export type StructuredPrevisitInsightsBase = z.infer<
  typeof structuredPrevisitInsightsBaseSchema
>;

export const structuredPrevisitContentSchema =
  structuredPrevisitInsightsBaseSchema.extend({
    citations: z.array(structuredCitationSchema),
  });

export type StructuredPrevisitContent = z.infer<
  typeof structuredPrevisitContentSchema
>;

const previsitInsightsResponseSchema = z.object({
  id: z.string(),
  type: z.literal(AppointmentOutputArtifactTypesLookup.PREVISIT_INSIGHTS),
  content: structuredPrevisitContentSchema,
});

export const previsitInsightsStatusSchema = z.enum([
  'success',
  'no_data',
  'error',
]);

export type PrevisitInsightsStatus = z.infer<
  typeof previsitInsightsStatusSchema
>;

// Single source for the PVI advance window: the backend sweep auto-schedules
// generation this many minutes before start_date, and the web UI suppresses
// early auto-generation outside the same window. The two must not drift.
export const PREVISIT_INSIGHTS_ADVANCE_MINUTES = 30;

// Grace window after start_date during which a NOT_STARTED appointment is still
// recent enough to auto-generate PVI for; also drives the backend isAppointmentRecent window.
export const PREVISIT_INSIGHTS_RECENT_WINDOW_HOURS = 24;

// Single source for the statuses PVI may generate for, consumed by both the
// predicate below and the sweep candidate query's SQL allow-list so they can't
// drift. NOT_STARTED is further narrowed by the recency window in the predicate.
export const PREVISIT_INSIGHTS_GENERATABLE_STATUSES: readonly AppointmentStatusEnum[] =
  [
    AppointmentStatusTypesLookup.NOT_STARTED,
    AppointmentStatusTypesLookup.ACTIVE,
  ];

/**
 * Whether PVI generation is allowed for an appointment in the given status.
 * Shared by the web UI (suppress auto-trigger / disable regenerate) and the
 * api-nest backstop so the two cannot drift. Allows `ACTIVE` (mid-visit
 * generation is wanted) and `NOT_STARTED` whose `start_date` is parseable and
 * within the last `PREVISIT_INSIGHTS_RECENT_WINDOW_HOURS` (or in the future).
 * Blocks `COMPLETED`, `CANCELLED`, and stale/null/unparseable `NOT_STARTED`. A
 * null/undefined status (param omitted by legacy non-web callers) is allowed.
 */
export function canGeneratePrevisitInsights(
  status: AppointmentStatusEnum | null | undefined,
  startDate: string | Date | null | undefined,
  now: Date = new Date(),
): boolean {
  if (status == null) return true;
  if (!PREVISIT_INSIGHTS_GENERATABLE_STATUSES.includes(status)) return false;
  if (status === AppointmentStatusTypesLookup.ACTIVE) return true;
  if (status !== AppointmentStatusTypesLookup.NOT_STARTED) return false;

  if (startDate == null) return false;
  const startMs =
    startDate instanceof Date
      ? startDate.getTime()
      : new Date(startDate).getTime();
  if (Number.isNaN(startMs)) return false;

  const cutoffMs =
    now.getTime() - PREVISIT_INSIGHTS_RECENT_WINDOW_HOURS * 60 * 60 * 1000;
  return startMs >= cutoffMs;
}

export const previsitInsightsMetadataSchema = z.object({
  format: z.string(),
  status: previsitInsightsStatusSchema,
  promptTemplateId: z.string().nullable().optional(),
  promptVariantNumber: z.string().nullable().optional(),
  generatedAt: z.string().optional(),
  error: z.string().optional(),
  platform: platformTypesSchema.optional(),
  platformVersion: z.string().optional(),
  // The patient the insights were generated for. Used to invalidate stale
  // insights when an appointment's patient is reassigned (ENG-4601). Nullable:
  // legacy artifacts and sample-mode runs have no patient stamped.
  patientId: z.string().nullable().optional(),
});

export type PrevisitInsightsMetadata = z.infer<
  typeof previsitInsightsMetadataSchema
>;

export function createPrevisitInsightsMetadata(
  params: Omit<PrevisitInsightsMetadata, 'format'>,
): PrevisitInsightsMetadata {
  return { format: 'json-render', ...params };
}

const previsitInsightsComponentResponseSchema = z.object({
  id: z.string(),
  type: z.literal(AppointmentOutputArtifactTypesLookup.PREVISIT_INSIGHTS),
  content: specSchema,
  status: previsitInsightsStatusSchema.optional(),
  // The patient these insights were generated for. Lets clients invalidate
  // stale insights after an appointment is reassigned (ENG-4601). Additive
  // optional/nullable so old/new clients and backends interoperate; absent =>
  // old backend, treated as "unknown" (no invalidation).
  generatedForPatientId: z.string().nullable().optional(),
});

const appointmentOutputArtifactResponseSchema = previsitInsightsResponseSchema;

const appointmentOutputArtifactComponentResponseSchema =
  previsitInsightsComponentResponseSchema;

export type AppointmentOutputArtifactResponse = z.infer<
  typeof appointmentOutputArtifactResponseSchema
>;

export type AppointmentOutputArtifactComponentResponse = z.infer<
  typeof appointmentOutputArtifactComponentResponseSchema
>;

export type PrevisitInsightsResponse = z.infer<
  typeof previsitInsightsResponseSchema
>;

export type PrevisitInsightsComponentResponse = z.infer<
  typeof previsitInsightsComponentResponseSchema
>;

const createAppointmentOutputArtifactResponseSchema =
  previsitInsightsComponentResponseSchema;

export const artifactJobStatusResponseSchema = z.object({
  type: appointmentOutputArtifactTypeSchema,
  jobId: z.string(),
  status: z.nativeEnum(WorkerStatusTypesLookup),
  createdAt: z.string(),
});

export type ArtifactJobStatusResponse = z.infer<
  typeof artifactJobStatusResponseSchema
>;

export const appointmentOutputArtifactsContract = c.router(
  {
    createAppointmentOutputArtifact: {
      method: 'POST',
      path: '',
      body: createAppointmentOutputArtifactRequestSchema,
      responses: {
        200: createAppointmentOutputArtifactResponseSchema,
        201: artifactJobStatusResponseSchema,
        202: artifactJobStatusResponseSchema,
      },
      summary: 'Create an appointment output artifact',
    },
    getAppointmentOutputArtifact: {
      method: 'GET',
      path: ':type',
      responses: {
        200: appointmentOutputArtifactComponentResponseSchema,
      },
      summary:
        'Get an appointment output artifact by type (returns json-render Spec format)',
    },
  },
  {
    pathPrefix:
      '/:tenantId/appointments/:appointmentId/appointment-output-artifacts/',
  },
);

const regenerateUpcomingPrevisitInsightsRequestSchema = z.object({
  dryRun: z.boolean().optional(),
});

export type RegenerateUpcomingPrevisitInsightsRequest = z.infer<
  typeof regenerateUpcomingPrevisitInsightsRequestSchema
>;

const regenerateUpcomingPrevisitInsightsResponseSchema = z.object({
  count: z.number(),
  appointmentIds: z.array(z.string()),
});

export type RegenerateUpcomingPrevisitInsightsResponse = z.infer<
  typeof regenerateUpcomingPrevisitInsightsResponseSchema
>;

export const tenantAppointmentOutputArtifactsContract = c.router(
  {
    regenerateUpcomingPrevisitInsights: {
      method: 'POST',
      path: 'regenerate-upcoming-previsit-insights',
      body: regenerateUpcomingPrevisitInsightsRequestSchema,
      responses: {
        200: regenerateUpcomingPrevisitInsightsResponseSchema,
      },
      summary:
        'Force-regenerate PVI for the current user’s upcoming appointments in the next 24h that already have a PVI artifact and have not started yet. Pass `dryRun: true` to return the eligible count without scheduling any work.',
    },
  },
  {
    pathPrefix: '/:tenantId/appointment-output-artifacts/',
  },
);
